import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookOpen, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import type { SkillLibrary } from '../../generated/api/types.gen.js';
import {
  deleteToolLibraryMutation,
  listToolLibrariesOptions,
  listToolLibrariesQueryKey,
  syncToolLibraryMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  PublishToMarketplaceModal,
  SearchBar,
  Toolbar,
  useToast,
} from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { ToolLibraryModal } from './ToolLibraryModal.js';
import { formatDate } from './user-labels.js';

const SOURCE_LABEL: Record<SkillLibrary['sourceType'], string> = {
  git: 'Git repository',
  url: 'Manifest address',
  local: 'Local folder',
};

export const ToolLibrariesPage: React.FC = () => {
  const help = PAGE_HELP['skill-libraries'];
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [publishTarget, setPublishTarget] = useState<SkillLibrary | null>(null);
  const [deleting, setDeleting] = useState<SkillLibrary | null>(null);
  const [search, setSearch] = useState('');

  const { data, isPending, isError, refetch } = useQuery(listToolLibrariesOptions());
  const refresh = () => queryClient.invalidateQueries({ queryKey: listToolLibrariesQueryKey() });

  const sync = useMutation({
    ...syncToolLibraryMutation(),
    onSuccess: async () => {
      notify('Sync started');
      await refresh();
    },
    onError: () => notify('Could not sync the library', 'danger'),
  });
  const remove = useMutation({
    ...deleteToolLibraryMutation(),
    onSuccess: async () => {
      setDeleting(null);
      notify('Library deleted');
      await refresh();
    },
    onError: () => notify('Could not delete the library', 'danger'),
  });

  const needle = search.trim().toLowerCase();
  const libraries = (data?.data ?? []).filter(
    (lib) => !needle || lib.name.toLowerCase().includes(needle) || lib.description?.toLowerCase().includes(needle),
  );

  const addButton = (
    <Can policy="RequireSuperadmin">
      <Button onClick={() => setShowCreate(true)}>
        <Plus size={14} aria-hidden="true" />
        Add library
      </Button>
    </Can>
  );

  const columns: DataTableColumn<SkillLibrary>[] = [
    {
      key: 'name',
      header: 'Library',
      sortable: true,
      sortValue: (lib) => lib.name.toLowerCase(),
      render: (lib) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-heading">{lib.name}</span>
          {lib.description && <span className="block truncate text-xs text-muted">{lib.description}</span>}
          {lib.lastError && <span className="block truncate text-xs text-danger">{lib.lastError}</span>}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (lib) => (
        <div className="min-w-0">
          <span className="block text-sm text-body">{SOURCE_LABEL[lib.sourceType]}</span>
          {(lib.repoUrl ?? lib.localPath) && (
            <span className="block max-w-xs truncate font-mono text-xs text-muted">{lib.repoUrl ?? lib.localPath}</span>
          )}
        </div>
      ),
    },
    {
      key: 'tools',
      header: 'Tools',
      align: 'right',
      sortable: true,
      sortValue: (lib) => lib.skillCount ?? 0,
      render: (lib) => <span className="text-sm text-body">{lib.skillCount ?? 0}</span>,
    },
    {
      key: 'syncedAt',
      header: 'Last synced',
      sortable: true,
      sortValue: (lib) => lib.lastSyncedAt,
      render: (lib) => <span className="text-sm text-muted">{formatDate(lib.lastSyncedAt, 'Never')}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (lib) => (
        <Badge variant={lib.enabled ? 'success' : 'neutral'} dot>
          {lib.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '220px',
      render: (lib) => (
        <div className="flex items-center justify-end gap-1.5">
          <Can policy="CanManageWorkflows">
            <Button size="sm" variant="ghost" onClick={() => setPublishTarget(lib)}>
              <Upload size={14} aria-hidden="true" />
              Publish
            </Button>
          </Can>
          <Can policy="RequireSuperadmin">
            <Button
              size="sm"
              variant="secondary"
              loading={sync.isPending && sync.variables?.path.id === String(lib.id)}
              onClick={() => sync.mutate({ path: { id: String(lib.id) } })}
            >
              <RefreshCw size={14} aria-hidden="true" />
              Sync
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              aria-label={`Delete ${lib.name}`}
              onClick={() => setDeleting(lib)}
            >
              <Trash2 size={14} aria-hidden="true" />
            </Button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <ListPage
      title="Tool libraries"
      subtitle="Repositories of tools that agents can use."
      helpContent={help}
      actions={addButton}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load tool libraries"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={<Toolbar search={<SearchBar value={search} onChange={setSearch} placeholder="Search libraries…" />} />}
    >
      {!isError && (
        <DataTable
          columns={columns}
          rows={libraries}
          rowKey={(lib) => lib.id}
          loading={isPending}
          aria-label="Tool libraries"
          empty={
            <EmptyState
              icon={BookOpen}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
              action={addButton}
            />
          }
        />
      )}

      <ToolLibraryModal open={showCreate} onClose={() => setShowCreate(false)} />
      {publishTarget && (
        <PublishToMarketplaceModal
          entityType="tool"
          entityId={publishTarget.id}
          entityName={publishTarget.name}
          open
          onClose={() => setPublishTarget(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate({ path: { id: String(deleting.id) } })}
        title="Delete library"
        message={`Delete "${deleting?.name ?? ''}"? Its tools are removed from every agent that uses them.`}
        confirmLabel="Delete library"
        loading={remove.isPending}
      />
    </ListPage>
  );
};
